import { Stack, IconButton, IStackStyles, IButtonStyles, mergeStyleSets } from "@fluentui/react";
import * as React from "react";
import { useId } from "@fluentui/react-hooks";
import { DeferredTooltipHost } from "./DeferredTooltipHost";

/**
 * The styles interface
 */
export interface ICollapsePanelStyles {
  /**
   * The root stack styles
   */
  root?: IStackStyles;

  /**
   * The button styles
   */
  button?: IButtonStyles;

  /**
   * Styles for the collapsed content
   */
  content?: IStackStyles;
}

/**
 * Props for the collapse panel component
 */
export interface ICollapsePanelProps {
  /**
   * A value indicating whether the panel should currently appear expanded
   */
  expanded: boolean;

  /**
   * The styles for the component
   */
  styles?: ICollapsePanelStyles;

  /**
   * A callback to render the title row
   */
  renderTitleRow: () => React.ReactElement;

  /**
   * A delegate for setting the expand state
   */
  setExpanded: (expanded: boolean) => void;

  /**
   * The title text for the panel (used for accessibility)
   */
  titleText?: string;

  /**
   * The content to render inside the collapsible panel
   */
  children?: React.ReactNode;
}

/**
 * The collapse panel component
 *
 * @param props - The component props
 * @returns The markup
 */
export const CollapsePanel: React.FC<ICollapsePanelProps> = ({ expanded, setExpanded, renderTitleRow, styles, children, titleText }) => {
  const expandCollapseAction = expanded ? ClientStrings.CollapseNode : ClientStrings.ExpandNode;
  const contentId = useId("collapse-panel-content");
  const titleId = useId("collapse-panel-title");

  return (
    <Stack styles={mergeStyleSets({ root: { width: "100%" } }, styles?.root)}>
      <Stack horizontal verticalAlign="center">
        <DeferredTooltipHost content={expandCollapseAction}>
          <IconButton
            ariaLabel={expandCollapseAction}
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-describedby={titleText ? titleId : undefined}
            iconProps={{ iconName: expanded ? "ChevronUp" : "ChevronDown" }}
            styles={styles?.button}
            onClick={(): void => setExpanded(!expanded)}
          />
        </DeferredTooltipHost>
        <div id={titleId} aria-hidden={!titleText}>
          {renderTitleRow()}
        </div>
      </Stack>
      <Stack
        id={contentId}
        styles={mergeStyleSets(styles?.content, {
          root: { display: expanded ? "block" : "none" },
        })}
      >
        {children}
      </Stack>
    </Stack>
  );
};
